import { Injectable } from '@angular/core';
import { from, mergeMap, Observable, of, switchMap, tap, catchError } from 'rxjs';
import { ulid } from 'ulid';
import {
  DriversIntegrationTypes,
  FileData,
  FileUploadStatus
} from 'src/app/projects/document-scan/drivers-integrations/drivers-integrations.types';
import { AuthService } from '../auth.service';
import { MediaUploadService } from '../media-upload.service';
import { ToastV2Service } from '../toast-v2.service';
import { BackgroundTaskRuntimeService } from './background-task-runtime.service';
import { NotificationCenterService } from './notification-center.service';
import { TaskRef } from './notification-center.types';
import {
  buildProgressFromEntries,
  buildTaskStatusFromEntries,
  buildUploadEntries,
  buildUploadMessage,
  mapUploadEntryStatus
} from './upload-task.helpers';

@Injectable({
  providedIn: 'root'
})
export class MediaUploadNotificationsService {
  private readonly maxConcurrentUploads = 3;

  constructor(
    private readonly toastService: ToastV2Service,
    private readonly mediaUploadService: MediaUploadService,
    private readonly authService: AuthService,
    private readonly notificationCenter: NotificationCenterService,
    private readonly taskRuntime: BackgroundTaskRuntimeService
  ) {}

  startMediaScanProject(
    files: File[],
    projectId: string,
    fileMetadata: { fileId: string; path: string; name: string }[]
  ): void {
    const user = this.authService.getCurrentUser();
    const metadataMap = new Map(fileMetadata.map((metadata) => [metadata.name, metadata]));

    const uploadItems: FileData[] = files.map((file) => {
      const metadata = metadataMap.get(file.name);

      return {
        fileId: metadata?.fileId ?? ulid(),
        name: file.name,
        userId: user.uid,
        provider: DriversIntegrationTypes.LOCAL_PICKER,
        mimeType: file.type,
        modifiedTime: Date.now(),
        progress: 0,
        size: file.size,
        error: false,
        status: FileUploadStatus.PENDING,
        uploadType: 'media',
        backendPath: metadata?.path,
        file
      };
    });

    let taskRef!: TaskRef;
    taskRef = this.notificationCenter.startTask({
      trackingKey: `media-upload:${projectId}`,
      kind: 'upload',
      title: `Uploading media files`,
      message: 'Preparing media upload.',
      status: 'running',
      cancellable: true,
      route: `/dashboard/media-scan/${projectId}`,
      entries: buildUploadEntries(uploadItems),
      actionLabel: 'Open project',
      onCancel: async () => {
        await this.taskRuntime.cancelTask(taskRef.id);
      }
    });

    this.taskRuntime.registerTask(taskRef.id);
    this.taskRuntime.setCancelHandler(taskRef.id, async () => {
      this.cancelTask(taskRef);
    });

    const uploadSubscription = from(uploadItems)
      .pipe(mergeMap((item) => this.processMediaUpload(item, projectId, taskRef), this.maxConcurrentUploads))
      .subscribe({
        complete: () => {
          const currentTask = this.notificationCenter.items().find((item) => item.id === taskRef.id);
          const entries = currentTask?.type === 'task' ? currentTask.entries ?? [] : [];
          const hasErrors = entries.some((entry) => entry.status === 'error');
          const successCount = entries.filter((entry) => entry.status === 'success').length;

          if (hasErrors) {
            taskRef.fail(`Upload completed with errors. ${successCount}/${entries.length} files uploaded successfully.`, {
              entries,
              progress: buildProgressFromEntries(entries),
              route: `/dashboard/media-scan/${projectId}`,
              actionLabel: 'Open project'
            });
            this.toastService.callErrorToast(
              `Upload completed with errors. ${successCount}/${entries.length} files uploaded successfully.`
            );
          } else {
            taskRef.complete({
              entries,
              progress: buildProgressFromEntries(entries),
              message: 'All files uploaded successfully.',
              route: `/dashboard/media-scan/${projectId}`,
              actionLabel: 'Open project'
            });
            this.toastService.callSuccessToast('All files uploaded successfully!');
          }

          this.taskRuntime.cleanupTask(taskRef.id);
        },
        error: () => {
          this.taskRuntime.cleanupTask(taskRef.id);
        }
      });

    this.taskRuntime.attachSubscription(taskRef.id, uploadSubscription);
  }

  private processMediaUpload(item: FileData, projectId: string, taskRef: TaskRef): Observable<void> {
    this.updateMediaEntry(taskRef, item.fileId, {
      status: FileUploadStatus.GETTING_URL,
      progress: item.progress
    });

    const filename = item.backendPath ?? this.mediaUploadService.normalizeFilename(projectId, item.name);
    return this.mediaUploadService
      .getPresignedUrl(
        {
          filename,
          fileType: item.mimeType
        },
        projectId
      )
      .pipe(
        switchMap((response) => {
          this.updateMediaEntry(taskRef, item.fileId, {
            status: FileUploadStatus.UPLOADING,
            r2Key: response.r2Key
          });

          return this.mediaUploadService.uploadMediaFile(response.uploadUrl, item.file!).pipe(
            tap((progress) => {
              this.updateMediaEntry(taskRef, item.fileId, {
                progress: progress.progress,
                status: progress.completed ? FileUploadStatus.UPLOADED : FileUploadStatus.UPLOADING
              });
            }),
            catchError((error) => {
              this.updateMediaEntry(taskRef, item.fileId, {
                status: FileUploadStatus.ERROR,
                error: error.message || 'Upload failed'
              });
              return of(void 0);
            })
          );
        }),
        catchError((error) => {
          this.updateMediaEntry(taskRef, item.fileId, {
            status: FileUploadStatus.ERROR,
            error: error.message || 'Failed to get upload URL'
          });
          return of(void 0);
        }),
        switchMap(() => of(void 0))
      );
  }

  private updateMediaEntry(taskRef: TaskRef, fileId: string, updates: Partial<FileData>): void {
    const currentTask = this.notificationCenter.items().find((item) => item.id === taskRef.id);
    if (!currentTask || currentTask.type !== 'task') {
      return;
    }

    const nextEntries = (currentTask.entries ?? []).map((entry) =>
      entry.id === fileId
        ? {
            ...entry,
            status: mapUploadEntryStatus(updates.status ?? FileUploadStatus.PENDING),
            progress: updates.progress ?? entry.progress,
            errorMessage: typeof updates.error === 'string' ? updates.error : entry.errorMessage
          }
        : entry
    );

    taskRef.update({
      entries: nextEntries,
      progress: buildProgressFromEntries(nextEntries),
      status: buildTaskStatusFromEntries(nextEntries),
      message: buildUploadMessage(nextEntries, 'media files')
    });
  }

  private cancelTask(taskRef: TaskRef): void {
    const currentTask = this.notificationCenter.items().find((item) => item.id === taskRef.id);
    const entries = currentTask?.type === 'task'
      ? (currentTask.entries ?? []).map((entry) =>
          entry.status === 'queued' || entry.status === 'running'
            ? { ...entry, status: 'canceled' as const }
            : entry
        )
      : [];

    taskRef.update({
      status: 'canceled',
      cancellable: false,
      entries,
      progress: buildProgressFromEntries(entries),
      message: 'Media upload canceled.',
      unread: true
    });
    this.toastService.callInformationToast('Files upload cancelled successfully!');
  }
}
